import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Loader2, Plus, ShieldOff, Trash2 } from "lucide-react";

import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../components/ui/select";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../../components/ui/dialog";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../../components/ui/table";

import CredencialRevelada from "../../components/CredencialRevelada";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const PRAZOS = [
	{ valor: "30d", rotulo: "30 dias" },
	{ valor: "90d", rotulo: "90 dias" },
	{ valor: "1y", rotulo: "1 ano" },
	{ valor: "never", rotulo: "Sem validade" },
];

const data = (valor) =>
	valor ? new Date(valor).toLocaleDateString("pt-BR") : null;

const quando = (valor) =>
	valor ? new Date(valor).toLocaleString("pt-BR") : "nunca";

/**
 * Tokens de acesso à API REST.
 *
 * Antes era um único token por empresa, guardado em texto puro e sem validade:
 * quem saísse da equipe levava a chave, e trocá-la derrubaria todas as
 * integrações ao mesmo tempo. Agora cada integração tem o seu, com nome,
 * prazo, registro de último uso e revogação individual.
 */
const GerenciadorDeTokens = () => {
	const [tokens, setTokens] = useState([]);
	const [carregando, setCarregando] = useState(true);
	const [modalAberto, setModalAberto] = useState(false);
	const [novo, setNovo] = useState({ name: "", prazo: "never" });
	const [salvando, setSalvando] = useState(false);
	const [revelado, setRevelado] = useState(null);

	const carregar = useCallback(async () => {
		setCarregando(true);
		try {
			const { data: resposta } = await api.get("/api-tokens");
			setTokens(resposta.tokens || []);
		} catch (err) {
			toastError(err);
		}
		setCarregando(false);
	}, []);

	useEffect(() => {
		carregar();
	}, [carregar]);

	const criar = async (e) => {
		e.preventDefault();
		setSalvando(true);
		try {
			const { data: criado } = await api.post("/api-tokens", novo);
			setModalAberto(false);
			setNovo({ name: "", prazo: "never" });
			setRevelado(criado.valor);
			await carregar();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	const revogar = async (token) => {
		if (
			!window.confirm(
				`Revogar "${token.name}"? Qualquer sistema usando este token para de funcionar na hora.`,
			)
		)
			return;

		try {
			await api.post(`/api-tokens/${token.id}/revoke`);
			toast.success("Token revogado.");
			await carregar();
		} catch (err) {
			toastError(err);
		}
	};

	const excluir = async (token) => {
		if (
			!window.confirm(
				`Excluir "${token.name}" da lista? O registro de quando ele foi usado some junto.`,
			)
		)
			return;

		try {
			await api.delete(`/api-tokens/${token.id}`);
			toast.success("Token excluído.");
			await carregar();
		} catch (err) {
			toastError(err);
		}
	};

	const estadoDe = (token) => {
		if (token.revokedAt) return { rotulo: "Revogado", cor: "destructive" };
		if (token.expirado) return { rotulo: "Vencido", cor: "destructive" };
		return { rotulo: "Válido", cor: "default" };
	};

	return (
		<div className="space-y-3 rounded-xl border bg-card p-4">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<div>
					<h2 className="text-sm font-semibold">Tokens de acesso</h2>
					<p className="text-xs text-muted-foreground">
						Um por integração. Assim dá para desligar a que saiu do ar sem
						derrubar as outras.
					</p>
				</div>

				<Button size="sm" onClick={() => setModalAberto(true)}>
					<Plus className="h-4 w-4" />
					Gerar token
				</Button>
			</div>

			<div className="overflow-x-auto rounded-lg border">
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>Nome</TableHead>
							<TableHead>Início do token</TableHead>
							<TableHead>Estado</TableHead>
							<TableHead>Validade</TableHead>
							<TableHead>Último uso</TableHead>
							<TableHead className="text-right">Ações</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{carregando && (
							<TableRow>
								<TableCell colSpan={6} className="py-6 text-center text-sm">
									Carregando…
								</TableCell>
							</TableRow>
						)}

						{!carregando && !tokens.length && (
							<TableRow>
								<TableCell colSpan={6} className="py-8 text-center">
									<p className="text-sm font-medium">Nenhum token ainda.</p>
									<p className="mt-1 text-sm text-muted-foreground">
										Gere um para o sistema que vai mandar mensagem por aqui.
									</p>
								</TableCell>
							</TableRow>
						)}

						{tokens.map((token) => {
							const estado = estadoDe(token);

							return (
								<TableRow key={token.id}>
									<TableCell className="font-medium">
										{token.name}
										{token.criadoPor && (
											<span className="block text-xs text-muted-foreground">
												por {token.criadoPor}
											</span>
										)}
									</TableCell>

									<TableCell className="font-mono text-xs">
										{token.prefix}…
									</TableCell>

									<TableCell>
										<Badge variant={estado.cor}>{estado.rotulo}</Badge>
									</TableCell>

									<TableCell className="text-xs">
										{data(token.expiresAt) || "sem validade"}
									</TableCell>

									<TableCell className="whitespace-nowrap text-xs">
										{quando(token.lastUsedAt)}
										{token.lastUsedIp && (
											<span className="block text-muted-foreground">
												de {token.lastUsedIp}
											</span>
										)}
									</TableCell>

									<TableCell className="text-right">
										<div className="flex justify-end gap-0.5">
											{!token.revokedAt && (
												<Button
													variant="ghost"
													size="icon"
													title="Revogar"
													onClick={() => revogar(token)}
												>
													<ShieldOff className="h-4 w-4" />
												</Button>
											)}
											<Button
												variant="ghost"
												size="icon"
												title="Excluir da lista"
												onClick={() => excluir(token)}
											>
												<Trash2 className="h-4 w-4" />
											</Button>
										</div>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</div>

			<Dialog open={modalAberto} onOpenChange={(v) => !v && setModalAberto(false)}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Gerar token</DialogTitle>
						<DialogDescription>
							O valor aparece uma vez só, logo depois de criar.
						</DialogDescription>
					</DialogHeader>

					<form onSubmit={criar} className="space-y-4">
						<div>
							<Label htmlFor="token-name">Para que serve</Label>
							<Input
								id="token-name"
								required
								minLength={2}
								placeholder="ERP do financeiro"
								value={novo.name}
								onChange={(e) => setNovo({ ...novo, name: e.target.value })}
							/>
							<p className="mt-1 text-xs text-muted-foreground">
								Um nome que você reconheça daqui a um ano, quando precisar
								decidir se pode revogar.
							</p>
						</div>

						<div>
							<Label htmlFor="token-prazo">Validade</Label>
							<Select
								value={novo.prazo}
								onValueChange={(v) => setNovo({ ...novo, prazo: v })}
							>
								<SelectTrigger id="token-prazo">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{PRAZOS.map((p) => (
										<SelectItem key={p.valor} value={p.valor}>
											{p.rotulo}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<p className="mt-1 text-xs text-muted-foreground">
								Com prazo, o token para de funcionar sozinho na data. Sem
								validade é prático e é o que a maioria das integrações de
								servidor usa.
							</p>
						</div>

						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => setModalAberto(false)}
							>
								Cancelar
							</Button>
							<Button type="submit" disabled={salvando}>
								{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
								Gerar
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>

			<CredencialRevelada
				aberto={Boolean(revelado)}
				titulo="Token gerado"
				valor={revelado || ""}
				descricao="Use no cabeçalho Authorization: Bearer, nas chamadas à API de envio."
				aoFechar={() => setRevelado(null)}
			/>
		</div>
	);
};

export default GerenciadorDeTokens;
